import {MOUSE} from "three";
import React, {useEffect, useState} from "react";
import {ipcRenderer, storage} from "./electron-modules";
import { Settings } from "http2";
import {
  Button, Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  List,
  ListItem, Radio,
  RadioGroup
} from "@material-ui/core";
import Divider from "@material-ui/core/Divider";
import Typography from "@material-ui/core/Typography";
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import { ViewerSettingsSlider } from "./settings-drawer";
import {ArrowTooltip} from "./tooltips";
import { Viewer } from "./viewer/js/main";

export enum ControlType {
  Encompass,
  Reverse,
  Autocad
}

export const ControlModes = {
  0: { ORBIT: MOUSE.LEFT, ZOOM: MOUSE.MIDDLE, PAN: MOUSE.RIGHT },
  1: { ORBIT: MOUSE.RIGHT, ZOOM: MOUSE.MIDDLE, PAN: MOUSE.LEFT },
  2: { ORBIT: -1, ZOOM: -1, PAN: MOUSE.MIDDLE }
};

export const initialGlobalSettingsState = {
  orthoQuality: 0.5,
  orthoOpacity: 1,
  controlType: ControlType.Encompass
} as Settings;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    listItem: {
      width: '100%',
      paddingRight: '5px',
      marginBottom: theme.spacing(0.5)
    },
    boldTypography: {
      fontWeight: 'bold',
      display: "inline-block",
      fontSize: '1em'
    },
    controlTooltip: {
      whiteSpace: 'pre-line'
    }
  })
);

const loadGlobalSettings = () => {
  const result = storage.get("global-settings");
  const globalSettings = result.status ? result.data : {};

  return {
    ...initialGlobalSettingsState,
    ...globalSettings
  };
};

export const getGlobalSettings = (key) => {
  return loadGlobalSettings()[key];
};

export const GlobalSettings = (props) => {
  const viewer = props.viewer as Viewer;

  const [globalSettings, setGlobalSettings] = useState({});
  const [open, setOpen] = useState(false);
  const classes = useStyles();

  const setFunction = (value, key) => {
    let vals = {...globalSettings};
    vals[key] = value;
    setGlobalSettings(vals);
  };

  const handleSave = () => {
    storage.set("global-settings", {...globalSettings});
    setOpen(false);
  };

  useEffect(() => {
    // Load global settings from file on initial load
    const settings = loadGlobalSettings();
    setGlobalSettings(settings);

    // Apply initial values
    viewer?.setOrthoOpacity(settings.orthoOpacity);
    viewer?.setControlType(settings.controlType);
  }, [viewer]);

  useEffect(() => {
    ipcRenderer?.removeAllListeners("open-global-settings");
    ipcRenderer?.on("open-global-settings", () => {
      setOpen(true);
    });
  });

  useEffect(() => {
    if (open) {
      setGlobalSettings(loadGlobalSettings());
    }
  }, [open]);


  return (
    <Dialog fullWidth maxWidth="xs" onClose={() => {setOpen(false);}} open={open}>
      <DialogTitle id="alert-dialog-title">{"Viewer Settings"}</DialogTitle>
      <DialogContent>
        <Divider variant="middle" />

        {/* Global Settings */}
        <List
          dense
          aria-labelledby="nested-list-subheader"
        >
          {/* Control Type */}
          <ListItem>
            <div className={classes.listItem}>
              <FormControl>
                <FormLabel className={classes.boldTypography} focused>Control Type</FormLabel>
                <RadioGroup
                  row
                  onChange={(event) => {
                    const value = Number(event.target.value);
                    setFunction(value, 'controlType');
                    viewer?.setControlType(value);
                  }}
                  value={globalSettings['controlType']}
                >
                  <ArrowTooltip
                    title={<span className={classes.controlTooltip}>{
                      "Orbit: Left mouse button\n" +
                      "Zoom: Mouse scroll\n" +
                      "Pan: Right mouse button"}
                    </span>}
                    placement="top">
                    <FormControlLabel value={0} control={<Radio />} label="Encompass" />
                  </ArrowTooltip>
                  <ArrowTooltip
                    title={<span className={classes.controlTooltip}>{
                      "Orbit: Right mouse button\n" +
                      "Zoom: Mouse scroll\n" +
                      "Pan: Left mouse button"}
                    </span>}
                    placement="top">
                    <FormControlLabel value={1} control={<Radio />} label="Reverse" />
                  </ArrowTooltip>
                  <ArrowTooltip
                    title={<span className={classes.controlTooltip}>{
                      "Orbit: Shift + middle button\n" +
                      "Zoom: Mouse scroll\n" +
                      "Pan: Middle mouse button"}
                    </span>}
                    placement="top">
                    <FormControlLabel value={2} control={<Radio />} label="Autocad" />
                  </ArrowTooltip>
                </RadioGroup>
              </FormControl>
            </div>
          </ListItem>

          <ListItem>
            <div className={classes.listItem}>
              <Typography className={classes.boldTypography} gutterBottom>Orthomosaic Settings</Typography>
            </div>
          </ListItem>

          {/* Ortho opacity */}
          <ListItem>
            <div className={classes.listItem}>
              <Typography gutterBottom>
                Opacity
              </Typography>
              <ViewerSettingsSlider
                color={"secondary"}
                min={0}
                max={1}
                step={0.01}
                value={globalSettings['orthoOpacity']}
                magnitude={100}
                endAdornment={"%"}
                labelFunction={(value) => {
                  return +(value*100).toFixed(1)+"%";
                }}
                setFunction={(value) => {
                  setFunction(value, "orthoOpacity");
                }}
                updateFunction={(value) => {
                  setFunction(value, "orthoOpacity");
                  viewer?.setOrthoOpacity(value);
                }}
              />
            </div>
          </ListItem>

          {/* Ortho quality */}
          <ListItem>
            <div className={classes.listItem}>
              <Typography gutterBottom>
                Import Quality
              </Typography>
              <ViewerSettingsSlider
                color={"secondary"}
                min={0.25}
                max={0.75}
                step={0.01}
                value={globalSettings['orthoQuality']}
                magnitude={100}
                endAdornment={"%"}
                labelFunction={(value) => {
                  return +(value*100).toFixed(1)+"%";
                }}
                setFunction={(value) => {
                  setFunction(value, "orthoQuality");
                }}
                updateFunction={(value) => {
                  setFunction(value, "orthoQuality");
                }}
              />
            </div>
          </ListItem>

        </List>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => {setOpen(false);}} color="primary">
         Cancel
        </Button>

        <Button onClick={handleSave} color="primary">
          Apply Settings
        </Button>
      </DialogActions>

    </Dialog>
  );
};